/*
 * Diamond selector - Build Your Ring step.
 * Paired with snippets/diamond-selector.liquid.
 *
 * Diamond data is fetched one shape at a time from the Shopify section
 * sections/byr-diamond-feed.liquid, which renders a JSON array of that product's
 * variants (paginated 100 at a time). The chosen diamond is posted to the cart as a
 * second line item, then the ring's own product form is submitted so the existing
 * product-form handler renders the cart drawer.
 *
 * Filtering is handled by DiamondFilterSelector (markup lives in the liquid file):
 * a single filter-state object drives colour / clarity / carat / price matching,
 * is mirrored into the URL, and is read back on load and on popstate.
 *
 * Results are rendered as one horizontal card per stone - image, badge, specs line,
 * price (or the difference against the selected stone) and an action - never as an
 * HTML table, so nothing scrolls sideways on a phone. Each card is a single
 * role="button" row with a nested clear button for the selected stone.
 */
(function () {
  'use strict';

  if (customElements.get('diamond-selector')) return;

  var FEED_SECTION = 'byr-diamond-feed';
  var PAGE_ROWS = 100;
  var MAX_PAGES = 8;

  /* Sort orders - lower ranks sort first. */
  var COLOR_ORDER = { D: 1, E: 2, F: 3, G: 4, H: 5, I: 6, J: 7 };
  var CLARITY_ORDER = { FL: 0, IF: 1, VVS1: 2, VVS2: 3, VS1: 4, VS2: 5, SI1: 6, SI2: 7 };

  /* Education scales, shown worst to best, exactly as a grading report lists them. */
  var COLOR_SCALE = ['J', 'I', 'H', 'G', 'F', 'E', 'D'];
  var CLARITY_SCALE = ['SI1', 'VS2', 'VS1', 'VVS2', 'VVS1', 'IF', 'FL'];

  /* Display order for the filter value buttons, as specified. */
  var VALUE_ORDER = {
    color: ['D', 'E', 'F', 'G', 'H', 'I', 'J'],
    clarity: ['SI1', 'SI2', 'VS2', 'VS1', 'VVS2', 'VVS1', 'IF', 'FL']
  };

  /* Centre-stone shapes we stock, and how they read in a title. */
  var SHAPE_WORDS = ['round', 'oval', 'emerald', 'marquise', 'pear', 'princess', 'cushion', 'asscher', 'radiant', 'heart'];
  var SHAPE_LABELS = {
    round: 'Round',
    oval: 'Oval',
    emerald: 'Emerald',
    marquise: 'Marquise',
    pear: 'Pear',
    princess: 'Princess',
    cushion: 'Cushion',
    asscher: 'Asscher',
    radiant: 'Radiant',
    heart: 'Heart'
  };

  function wordRe(word) {
    return new RegExp('\\b' + word + '\\b');
  }

  /* The ring's own shape, read from its title. "X Cut Center Stone" wins - that is the centre
     stone, and any shape named before it describes accent stones or the design. */
  function shapeFromTitle(title) {
    var text = String(title || '').toLowerCase();
    if (!text) return '';

    var at = text.indexOf('center stone');
    if (at > -1) {
      var scope = text.slice(0, at);
      var closest = -1;
      var found = '';
      for (var i = 0; i < SHAPE_WORDS.length; i++) {
        var word = SHAPE_WORDS[i];
        var here = scope.lastIndexOf(word);
        if (here > -1 && here > closest && wordRe(word).test(scope)) {
          closest = here;
          found = word;
        }
      }
      if (found) return found;
    }

    var first = -1;
    var hit = '';
    for (var j = 0; j < SHAPE_WORDS.length; j++) {
      var candidate = SHAPE_WORDS[j];
      var pos = text.indexOf(candidate);
      if (pos > -1 && (first === -1 || pos < first) && wordRe(candidate).test(text)) {
        first = pos;
        hit = candidate;
      }
    }
    return hit;
  }

  var URL_KEYS = ['carat_min', 'carat_max', 'color', 'clarity', 'price_min', 'price_max'];
  var GROUPS = ['carat', 'price'];
  var CARAT_STEP = 0.01;
  var PRICE_STEP = 1;
  var URL_DEBOUNCE = 300;

  /* Shape data and the current selection survive any re-render of the product info. */
  var shapeCache = Object.create(null);
  var shapeLoading = Object.create(null);
  var selection = Object.create(null);

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function stripSectionWrapper(text) {
    var open = text.indexOf('>');
    var close = text.lastIndexOf('</div>');
    if (open > -1 && close > open && text.slice(0, open).indexOf('shopify-section') !== -1) {
      return text.slice(open + 1, close);
    }
    return text;
  }

  function rank(map, a, b) {
    var ra = map[a] || 99;
    var rb = map[b] || 99;
    if (ra !== rb) return ra - rb;
    return String(a || '').localeCompare(String(b || ''));
  }

  function clamp(value, min, max) {
    if (value < min) return min;
    if (value > max) return max;
    return value;
  }

  function nearly(a, b) {
    if (a == null || b == null) return a === b;
    return Math.abs(Number(a) - Number(b)) < 0.005;
  }

  function roundTo(value, step) {
    return Math.round(value / step) * step;
  }

  function formatNumber(value, step) {
    if (step >= 1) return String(Math.round(value));
    var scaled = Math.round(value / step) * step;
    return String(Math.round(scaled * 100) / 100);
  }

  function parseList(raw) {
    if (!raw) return [];
    return String(raw)
      .split(',')
      .map(function (part) {
        return part.trim().toUpperCase();
      })
      .filter(Boolean);
  }

  /* Shopify money template ("Rs.{{amount}}", "${{amount}}", ...) -> formatted string. */
  function formatWithDelimiters(number, precision, thousands, decimal) {
    var fixed = Math.abs(number).toFixed(precision);
    var parts = fixed.split('.');
    var units = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    var cents = parts[1] ? decimal + parts[1] : '';
    return (number < 0 ? '-' : '') + units + cents;
  }

  function formatMoney(cents, format, currency) {
    var template = String(format || '');
    var match = template.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return (currency ? currency + ' ' : '') + formatWithDelimiters(Number(cents) / 100, 2, ',', '.');

    var precision = 2;
    var thousands = ',';
    var decimal = '.';
    switch (match[1]) {
      case 'amount_no_decimals':
        precision = 0;
        break;
      case 'amount_with_comma_separator':
        thousands = '.';
        decimal = ',';
        break;
      case 'amount_no_decimals_with_comma_separator':
        precision = 0;
        thousands = '.';
        decimal = ',';
        break;
      case 'amount_with_apostrophe_separator':
        thousands = "'";
        break;
      default:
        break;
    }

    return template.replace(match[0], formatWithDelimiters(Number(cents) / 100, precision, thousands, decimal));
  }

  /* "5.43 x 5.33 - 3.64" -> "5.43 × 5.33mm" (length, width - depth). */
  function dimensions(row) {
    var numbers = String(row.measure || '').match(/\d+(?:\.\d+)?/g);
    if (!numbers || numbers.length < 2) return '';
    return numbers[0] + ' × ' + numbers[1] + 'mm';
  }

  /* How far the stone sits from the reference carat before the hint is worth showing. */
  var CARAT_HINT_MAX = 0.1;

  customElements.define(
    'diamond-selector',
    class DiamondSelector extends HTMLElement {
      constructor() {
        super();
        this.rows = [];
        this.shape = '';
        this.shapeLabel = '';
        this.loadedShape = null;
        this.limit = 0;
        this.pageSize = parseInt(this.dataset.pageSize, 10) || 12;
        this.busy = false;
        this.sort = 'carat-asc';
        this.sortTouched = false;

        /* Single filter state - the only source of truth for filtering. */
        this.filterState = {
          activeFilter: 'carat',
          carat: { min: null, max: null },
          color: { values: [] },
          clarity: { values: [] },
          price: { min: null, max: null }
        };

        /* Bounds come from the loaded inventory, never from a hard-coded ceiling. */
        this.bounds = { carat: { min: 0, max: 10 }, price: { min: 0, max: 10000 } };
        this.defaults = null;
        this.boundsReady = false;

        this.titleShape = shapeFromTitle(this.dataset.productTitle);
        this.productId = this.dataset.productId || '';
        this.selected = null;

        this._url = null;
        this._urlApplied = false;
        this._urlPush = false;
        this._urlTimer = null;
        this._onPopState = this._onPopState.bind(this);
      }

      connectedCallback() {
        if (this._init) return;
        this._init = true;

        /* The block lives inside .product__info-wrapper, which the theme animates with
           `transform: translateY(2rem)`. A transformed ancestor becomes the containing block
           for `position: fixed`, so an overlay left in place is trapped inside the product
           information column instead of covering the page. Move it to <body> and hand the
           stylesheet a body-level id so the same rules keep applying. */
        this.relocateViewer();

        this.tabsEl = this.querySelector('.dbs__tabs');
        this.rowsEl = this.querySelector('[data-rows]');
        this.skeletonEl = this.querySelector('[data-skeletons]');
        this.countEl = this.querySelector('[data-count]');
        this.loadingEl = this.querySelector('[data-loading]');
        this.moreEl = this.querySelector('[data-more]');
        this.resetEls = [].slice.call(this.querySelectorAll('[data-reset]'));
        this.resetEl = this.resetEls[0] || null;
        this.emptyEl = this.querySelector('[data-empty]');
        this.errorEl = this.querySelector('[data-error]');
        this.errorTextEl = this.querySelector('[data-error-text]');
        this.retryEl = this.querySelector('[data-retry]');
        this.statusEl = this.querySelector('[data-status]');
        this.addEl = this.querySelector('[data-add]');
        this.selectedEl = this.querySelector('[data-selected]');
        this.panelEl = this.querySelector('[data-panel]');
        this.openEl = this.querySelector('[data-open]');
        this.summaryEl = this.querySelector('[data-open-summary]');

        this.modalDialogEl = this.mv('[data-modal-dialog]');
        this.modalScrollEl = this.mv('[data-modal-scroll]');
        this.modalImgEl = this.mv('[data-modal-img]');
        this.modalVideoEl = this.mv('[data-modal-video]');
        this.modalZoomEl = this.mv('[data-modal-zoom]');
        this.modalFallbackEl = this.mv('[data-modal-fallback]');
        this.modalHintEl = this.mv('[data-modal-hint]');
        this.modalTitleEl = this.mv('[data-modal-title]');
        this.modalSubtitleEl = this.mv('[data-modal-subtitle]');
        this.modalBadgeEl = this.mv('[data-modal-badge]');
        this.modalCertBadgeEl = this.mv('[data-modal-cert-badge]');
        this.modalPriceEl = this.mv('[data-modal-price]');
        this.modalQuickEl = this.mv('[data-modal-quick]');
        this.modalWhyEl = this.mv('[data-modal-why]');
        this.modalPropEl = this.mv('[data-modal-proportions]');
        this.modalTipWhyEl = this.mv('[data-modal-tip-why]');
        this.modalTipPropEl = this.mv('[data-modal-tip-proportions]');
        this.modalSpecsEl = this.mv('[data-modal-specs]');
        this.modalCtaEl = this.mv('[data-choose]');
        this.modalNoticeEl = this.mv('[data-modal-notice]');
        this.modalCertBtnEl = this.mv('[data-modal-cert-btn]');
        this.modalTabsEl = this.mv('[data-modal-tabs]');
        this.modalPriceBoxEl = this.mv('[data-modal-price-box]');
        this.activeDiamond = null;
        this.modalOpener = null;
        this.modalOpen = false;
        this.zoomOn = false;
        this._onDocKey = this._onDocKey.bind(this);

        this.filterRoot = this.querySelector('[data-filter]');
        this.filterTabs = [].slice.call(this.querySelectorAll('[data-ff]'));
        this.filterPanels = [].slice.call(this.querySelectorAll('[data-fpanel]'));

        var saved = selection[this.productId];
        if (saved && saved.row) {
          this.selected = saved.row;
          this.shape = saved.shape;
          this.shapeLabel = saved.shapeLabel;
        }
        if (!this.shape) {
          var first = this.shapeList[0] || '';
          this.shape = this.toKey(first);
          this.shapeLabel = first;
        }

        this._url = this.readUrl();

        this.buildTabs();
        this.bindEvents();
        this.bindFilterEvents();
        this.bindFormInterception();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this._unsubscribers = [
            subscribe(PUB_SUB_EVENTS.cartUpdate, this._onCartSettle.bind(this, false)),
            subscribe(PUB_SUB_EVENTS.cartError, this._onCartSettle.bind(this, true))
          ];
        }

        if (window.addEventListener) window.addEventListener('popstate', this._onPopState);

        this.syncOpenButton();
        if (this.selected) this.open();
      }

      disconnectedCallback() {
        if (this._unsubscribers) {
          this._unsubscribers.forEach(function (unsubscribe) {
            unsubscribe();
          });
          this._unsubscribers = null;
        }
        if (window.removeEventListener) window.removeEventListener('popstate', this._onPopState);
        if (document.removeEventListener) document.removeEventListener('keydown', this._onDocKey);
        if (this.modalOpen) document.documentElement.style.overflow = this._prevOverflow || '';
        clearTimeout(this._urlTimer);
      }

      _onCartSettle(failed) {
        if (!this.busy) return;
        this.setBusy(false);
        if (failed) this.setStatus('The ring could not be added. Your diamond is already in the cart.');
      }

      /* The ring's own shape comes from its title; the configured list is only a fallback. */
      get shapeList() {
        if (this.titleShape && SHAPE_LABELS[this.titleShape]) return [SHAPE_LABELS[this.titleShape]];
        return (this.dataset.shapes || '')
          .split(',')
          .map(function (s) {
            return s.trim();
          })
          .filter(Boolean);
      }

      toKey(label) {
        return String(label || '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
      }

      /* ------------------------------------------------------------------
         Open / close
         ------------------------------------------------------------------ */

      open() {
        if (this.panelEl) this.panelEl.hidden = false;
        this.syncOpenButton();
        if (this.loadedShape !== this.shape) this.loadShape(this.shape, this.shapeLabel);
        /* the panel is open, so the pinned column can no longer be sticky, whatever triggered it
           (including a restore on page load, which must not scroll) */
        if (document.body) document.body.classList.add('dbs-panel-open');
      }

      close() {
        if (this.panelEl) this.panelEl.hidden = true;
        this.syncOpenButton();
        this.releaseStickyColumn();
      }

      /* Bring the block into view when the shopper asks for it. Only ever called from the
         launch button, never on load, so the page never scrolls on its own. */
      revealBlock() {
        var self = this;
        var raf = window.requestAnimationFrame
          ? function (fn) { window.requestAnimationFrame(fn); }
          : function (fn) { setTimeout(fn, 16); };

        /* wait for the panel to lay out (and the skeletons to appear) before measuring */
        raf(function () {
          raf(function () {
            var rect = self.getBoundingClientRect();
            var viewport = window.innerHeight || document.documentElement.clientHeight;
            var header = 0;
            var stickyHeader = document.querySelector('.shopify-section-header--sticky, .site-header');
            if (stickyHeader) {
              var hs = window.getComputedStyle(stickyHeader);
              if (hs.position === 'sticky' || hs.position === 'fixed') {
                header = stickyHeader.getBoundingClientRect().height;
              }
            }

            /* only scroll when the block is not already comfortable on screen */
            var fullyVisible = rect.top >= header && rect.bottom <= viewport;
            if (fullyVisible) return;

            var target = (window.pageYOffset || document.documentElement.scrollTop || 0) + rect.top - header - 16;
            var reduced = false;
            if (window.matchMedia) {
              try { reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { reduced = false; }
            }
            if (window.scrollTo) {
              window.scrollTo({ top: Math.max(0, target), behavior: reduced ? 'auto' : 'smooth' });
            }
          });
        });
      }

      releaseStickyColumn() {
        if (document.body) document.body.classList.remove('dbs-panel-open');
      }

      syncOpenButton() {
        if (!this.openEl) return;
        var expanded = this.panelEl && !this.panelEl.hidden;
        this.openEl.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        this.openEl.textContent = expanded
          ? 'Hide diamond list'
          : this.selected
            ? this.dataset.selectedLabel || 'Change diamond'
            : this.dataset.openLabel || 'Choose your diamond';

        if (this.summaryEl) {
          if (this.selected) {
            this.summaryEl.textContent =
              this.selected.carat +
              ' ct ' +
              this.selected.color +
              ' ' +
              this.selected.clarity +
              ' ' +
              this.selected.shape;
            this.summaryEl.hidden = false;
          } else {
            this.summaryEl.textContent = '';
            this.summaryEl.hidden = true;
          }
        }
      }

      /* ------------------------------------------------------------------
         Shape tabs (unchanged behaviour)
         ------------------------------------------------------------------ */

      buildTabs() {
        var labels = this.shapeList;
        var self = this;

        /* One shape read from the title leaves nothing to choose between, so the row goes away. */
        if (labels.length < 2) {
          this.tabsEl.hidden = true;
          this.tabsEl.style.display = 'none';
          this.tabsEl.innerHTML = '';
          return;
        }

        this.tabsEl.hidden = false;
        this.tabsEl.style.display = '';
        this.tabsEl.innerHTML = labels
          .map(function (label, index) {
            var key = self.toKey(label);
            return (
              '<button type="button" class="dbs__tab' +
              (index === 0 ? ' is-active' : '') +
              '" role="tab" aria-selected="' +
              (index === 0 ? 'true' : 'false') +
              '" data-shape="' +
              esc(key) +
              '" data-label="' +
              esc(label) +
              '">' +
              esc(label) +
              '</button>'
            );
          })
          .join('');
        if (this.shape) this.markTab(this.shape);
      }

      markTab(shape) {
        var tabs = this.tabsEl.querySelectorAll('.dbs__tab');
        for (var i = 0; i < tabs.length; i++) {
          var on = tabs[i].dataset.shape === shape;
          tabs[i].classList.toggle('is-active', on);
          tabs[i].setAttribute('aria-selected', on ? 'true' : 'false');
        }
      }

      /* ------------------------------------------------------------------
         Events
         ------------------------------------------------------------------ */

      bindEvents() {
        var self = this;

        this.openEl.addEventListener('click', function () {
          if (self.panelEl.hidden) {
            self.open();
            self.revealBlock();
          } else {
            self.close();
          }
        });

        this.tabsEl.addEventListener('click', function (event) {
          var tab = event.target.closest('.dbs__tab');
          if (!tab || tab.classList.contains('is-active')) return;
          self.loadShape(tab.dataset.shape, tab.dataset.label);
        });

        this.querySelector('[data-f="sort"]').addEventListener('change', function (e) {
          self.sort = e.target.value;
          self.sortTouched = true;
          self.limit = self.pageSize;
          self.render();
        });

        this.rowsEl.addEventListener('click', function (event) {
          var clear = event.target.closest('[data-clear]');
          if (clear) {
            event.preventDefault();
            self.closeModal();
            self.clearSelection();
            return;
          }
          var row = event.target.closest('[data-pick]');
          if (!row) return;
          var pick = self.rows.find(function (item) {
            return String(item.id) === row.dataset.pick;
          });
          if (pick) self.openModal(pick, row);
        });

        this.rowsEl.addEventListener('keydown', function (event) {
          if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
          if (event.target !== event.target.closest('.dbs__result')) return;
          event.preventDefault();
          var row = event.target.closest('[data-pick]');
          if (!row) return;
          var pick = self.rows.find(function (item) {
            return String(item.id) === row.dataset.pick;
          });
          if (pick) self.openModal(pick, row);
        });

        this.bindModalEvents();

        this.querySelector('[data-unselect]').addEventListener('click', function () {
          self.clearSelection();
        });

        this.moreEl.addEventListener('click', function () {
          self.limit += self.pageSize;
          self.render();
        });

        this.resetEls.forEach(function (button) {
          button.addEventListener('click', function () {
            self.resetFilters();
          });
        });

        if (this.retryEl) {
          this.retryEl.addEventListener('click', function () {
            self.loadShape(self.shape, self.shapeLabel);
          });
        }

        if (this.addEl) {
          this.addEl.addEventListener('click', function () {
            self.addBoth();
          });
        }
      }

      /* --- DiamondFilterSelector wiring --- */

      bindFilterEvents() {
        var self = this;

        this.filterTabs.forEach(function (tab) {
          tab.addEventListener('click', function () {
            self.setActiveFilter(tab.dataset.ff);
          });
        });

        var tablist = this.querySelector('[data-filter] .elaris-diamond-filter__tabs');
        if (tablist) {
          tablist.addEventListener('keydown', function (event) {
            var keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
            if (keys.indexOf(event.key) === -1) return;
            var tabs = self.filterTabs;
            var index = tabs.indexOf(document.activeElement);
            if (index === -1) return;
            event.preventDefault();
            var next =
              event.key === 'ArrowRight' || event.key === 'ArrowDown'
                ? (index + 1) % tabs.length
                : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
                  ? (index - 1 + tabs.length) % tabs.length
                  : event.key === 'Home'
                    ? 0
                    : tabs.length - 1;
            tabs[next].focus();
            self.setActiveFilter(tabs[next].dataset.ff);
          });
        }

        GROUPS.forEach(function (group) {
          var slider = self.querySelector('[data-slider="' + group + '"]');
          if (!slider) return;

          slider.addEventListener('input', function (event) {
            var input = event.target;
            if (!input || input.tagName !== 'INPUT' || !input.dataset.range) return;
            self.setRange(group, input.dataset.range, parseFloat(input.value), true);
          });

          slider.addEventListener('change', function (event) {
            var input = event.target;
            if (!input || input.tagName !== 'INPUT' || !input.dataset.range) return;
            self.syncSlider(group);
            self.syncInputs(group, null);
            self.queueUrl(true);
          });

          /* Clicking the track moves whichever handle is nearer. */
          slider.addEventListener('click', function (event) {
            if (event.target.tagName === 'INPUT') return;
            var rect = slider.getBoundingClientRect();
            if (!rect.width) return;
            var ratio = clamp((event.clientX - rect.left) / rect.width, 0, 1);
            var b = self.bounds[group];
            var step = group === 'carat' ? CARAT_STEP : PRICE_STEP;
            var value = roundTo(b.min + ratio * (b.max - b.min), step);
            var state = self.filterState[group];
            var which = Math.abs(value - state.min) <= Math.abs(value - state.max) ? 'min' : 'max';
            self.setRange(group, which, value, true);
            self.syncSlider(group);
            self.syncInputs(group, null);
            var handle = slider.querySelector('[data-range="' + which + '"]');
            if (handle && handle.focus) handle.focus();
            self.queueUrl(true);
          });
        });

        ['cmin', 'cmax', 'pmin', 'pmax'].forEach(function (name) {
          var input = self.querySelector('[data-f="' + name + '"]');
          if (!input) return;
          var group = name.charAt(0) === 'c' ? 'carat' : 'price';
          var side = name === 'cmin' || name === 'pmin' ? 'min' : 'max';

          input.addEventListener('input', function (event) {
            var raw = event.target.value;
            var value = parseFloat(raw);
            if (raw === '' || isNaN(value)) return;
            self.setRange(group, side, value, true);
            self.syncSlider(group);
            /* Leave the field being typed in alone; only mirror to the other side. */
            self.syncInputs(group, side);
            self.onFilterChanged(false);
          });

          input.addEventListener('change', function () {
            self.syncSlider(group);
            self.syncInputs(group, null);
            self.queueUrl(true);
          });
        });

        ['color', 'clarity'].forEach(function (group) {
          var host = self.querySelector('[data-values="' + group + '"]');
          if (!host) return;
          host.addEventListener('click', function (event) {
            var button = event.target.closest('[data-value]');
            if (!button || button.disabled) return;
            var value = button.dataset.value;
            var list = self.filterState[group].values;
            var index = list.indexOf(value);
            if (index === -1) list.push(value);
            else list.splice(index, 1);
            var on = index === -1;
            button.classList.toggle('is-active', on);
            button.setAttribute('aria-pressed', on ? 'true' : 'false');
            self.limit = self.pageSize;
            self.onFilterChanged(true);
          });
        });
      }

      setActiveFilter(name) {
        if (!name) return;
        this.filterState.activeFilter = name;
        this.filterTabs.forEach(function (tab) {
          var on = tab.dataset.ff === name;
          tab.classList.toggle('is-active', on);
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        this.filterPanels.forEach(function (panel) {
          panel.hidden = panel.dataset.fpanel !== name;
        });
      }

      /* ------------------------------------------------------------------
         Feed
         ------------------------------------------------------------------ */

      fetchPage(shape, page) {
        var url =
          '/products/' +
          encodeURIComponent(shape) +
          '-lab-grown-diamonds?section_id=' +
          FEED_SECTION +
          '&page=' +
          page;
        return fetch(url, { credentials: 'same-origin' }).then(function (response) {
          if (!response.ok) throw new Error('Diamond feed returned HTTP ' + response.status);
          return response.text().then(function (text) {
            var data;
            try {
              data = JSON.parse(stripSectionWrapper(text));
            } catch (error) {
              throw new Error('Diamond feed was not valid JSON');
            }
            if (!Array.isArray(data)) throw new Error('Unexpected diamond feed payload');
            return data;
          });
        });
      }

      loadShape(shape, label) {
        this.shape = shape;
        this.shapeLabel = label || shape;
        this.markTab(shape);
        this.errorEl.hidden = true;
        this.emptyEl.hidden = true;
        this.setLoading(true);

        var self = this;
        var cached = shapeCache[shape];

        var promise = cached
          ? Promise.resolve(cached)
          : shapeLoading[shape] ||
            (shapeLoading[shape] = (function () {
              var collected = [];
              var page = 1;

              function next() {
                return self.fetchPage(shape, page).then(function (chunk) {
                  if (!chunk.length) return collected;
                  /* remember which feed page a stone came from: the detail viewer re-fetches
                     exactly that page to confirm the stone is still available */
                  chunk.forEach(function (row) {
                    row.page = page;
                  });
                  collected = collected.concat(chunk);
                  if (chunk.length < PAGE_ROWS || page >= MAX_PAGES) return collected;
                  page += 1;
                  return next();
                });
              }

              return next().then(
                function (all) {
                  delete shapeLoading[shape];
                  shapeCache[shape] = all;
                  return all;
                },
                function (error) {
                  delete shapeLoading[shape];
                  throw error;
                }
              );
            })());

        promise
          .then(function (rows) {
            if (self.shape !== shape) return;
            self.rows = rows;
            self.loadedShape = shape;
            self.limit = self.pageSize;
            self.applyBounds(rows);
            self.buildValueButtons();
            self.syncControls();
            self.restoreSelection(rows);
            self.render();
            self.setLoading(false);
          })
          .catch(function (error) {
            if (self.shape !== shape) return;
            self.rows = [];
            self.setLoading(false);
            self.countEl.textContent = '';
            if (self.errorTextEl) {
              self.errorTextEl.textContent =
                'Unable to load diamonds right now. Please try again in a moment.';
            } else {
              self.errorEl.textContent = 'Unable to load diamonds right now.';
            }
            self.errorEl.hidden = false;
            if (window.console && console.warn) console.warn('[diamond-selector]', error);
          });
      }

      setLoading(on) {
        if (this.loadingEl) this.loadingEl.hidden = !on;
        if (this.countEl) this.countEl.hidden = !!on;
        if (this.skeletonEl) this.skeletonEl.hidden = !on;
        if (this.rowsEl) this.rowsEl.hidden = !!on;
      }

      /* ------------------------------------------------------------------
         Filter state: bounds, defaults, URL
         ------------------------------------------------------------------ */

      applyBounds(rows) {
        if (!rows.length) return;

        var caratMin = Infinity;
        var caratMax = -Infinity;
        var priceMax = -Infinity;

        for (var i = 0; i < rows.length; i++) {
          var carat = Number(rows[i].carat);
          var price = Number(rows[i].price_c) / 100;
          if (!isNaN(carat)) {
            if (carat < caratMin) caratMin = carat;
            if (carat > caratMax) caratMax = carat;
          }
          if (!isNaN(price) && price > priceMax) priceMax = price;
        }
        if (caratMin === Infinity) return;

        var wasDefault = !this.defaults || this.isDefault();

        this.bounds = {
          carat: { min: Math.floor(caratMin * 100) / 100, max: Math.ceil(caratMax * 100) / 100 },
          price: { min: 0, max: Math.ceil(priceMax / PRICE_STEP) * PRICE_STEP }
        };

        this.defaults = {
          carat: {
            min: Math.max(1, this.bounds.carat.min),
            max: this.bounds.carat.max
          },
          price: { min: this.bounds.price.min, max: this.bounds.price.max },
          color: { values: [] },
          clarity: { values: [] }
        };

        if (wasDefault) {
          this.filterState.carat = { min: this.defaults.carat.min, max: this.defaults.carat.max };
          this.filterState.price = { min: this.defaults.price.min, max: this.defaults.price.max };
          this.filterState.color.values = [];
          this.filterState.clarity.values = [];
        } else {
          this.clampState();
        }

        this.boundsReady = true;
        this.applyUrlIfAny();
      }

      clampState() {
        var b = this.bounds;
        var s = this.filterState;
        s.carat.min = clamp(roundTo(Number(s.carat.min), CARAT_STEP), b.carat.min, b.carat.max);
        s.carat.max = clamp(roundTo(Number(s.carat.max), CARAT_STEP), b.carat.min, b.carat.max);
        if (s.carat.min > s.carat.max) {
          var swapCarat = s.carat.min;
          s.carat.min = s.carat.max;
          s.carat.max = swapCarat;
        }
        s.price.min = clamp(roundTo(Number(s.price.min), PRICE_STEP), b.price.min, b.price.max);
        s.price.max = clamp(roundTo(Number(s.price.max), PRICE_STEP), b.price.min, b.price.max);
        if (s.price.min > s.price.max) {
          var swapPrice = s.price.min;
          s.price.min = s.price.max;
          s.price.max = swapPrice;
        }
      }

      isDefault() {
        var d = this.defaults;
        if (!d) return true;
        var s = this.filterState;
        return (
          nearly(s.carat.min, d.carat.min) &&
          nearly(s.carat.max, d.carat.max) &&
          nearly(s.price.min, d.price.min) &&
          nearly(s.price.max, d.price.max) &&
          s.color.values.length === 0 &&
          s.clarity.values.length === 0
        );
      }

      readUrl() {
        try {
          var params = new URLSearchParams(window.location.search);
          var out = {};
          URL_KEYS.forEach(function (key) {
            var value = params.get(key);
            if (value != null) out[key] = value;
          });
          return out;
        } catch (error) {
          return {};
        }
      }

      applyUrlIfAny() {
        if (this._urlApplied || !this.boundsReady || !this._url) return;
        var source = this._url;
        var hasAny = URL_KEYS.some(function (key) {
          return source[key] != null;
        });
        this._urlApplied = true;
        if (!hasAny) return;

        var s = this.filterState;
        var b = this.bounds;
        var value;

        if (!isNaN((value = parseFloat(source.carat_min)))) {
          s.carat.min = clamp(value, b.carat.min, b.carat.max);
        }
        if (!isNaN((value = parseFloat(source.carat_max)))) {
          s.carat.max = clamp(value, b.carat.min, b.carat.max);
        }
        if (s.carat.min > s.carat.max) {
          var swapCarat = s.carat.min;
          s.carat.min = s.carat.max;
          s.carat.max = swapCarat;
        }

        if (!isNaN((value = parseFloat(source.price_min)))) {
          s.price.min = clamp(value, b.price.min, b.price.max);
        }
        if (!isNaN((value = parseFloat(source.price_max)))) {
          s.price.max = clamp(value, b.price.min, b.price.max);
        }
        if (s.price.min > s.price.max) {
          var swapPrice = s.price.min;
          s.price.min = s.price.max;
          s.price.max = swapPrice;
        }

        if (source.color) s.color.values = parseList(source.color);
        if (source.clarity) s.clarity.values = parseList(source.clarity);
      }

      _onPopState() {
        this._url = this.readUrl();
        this._urlApplied = false;
        this.applyUrlIfAny();
        this.syncControls();
        this.render();
      }

      queueUrl(push) {
        if (push) this._urlPush = true;
        clearTimeout(this._urlTimer);
        var self = this;
        this._urlTimer = setTimeout(function () {
          self.writeUrl(self._urlPush);
          self._urlPush = false;
        }, URL_DEBOUNCE);
      }

      writeUrl(push) {
        if (!this.boundsReady) return;
        var params;
        try {
          params = new URLSearchParams(window.location.search);
        } catch (error) {
          return;
        }
        URL_KEYS.forEach(function (key) {
          params.delete(key);
        });

        var s = this.filterState;
        var d = this.defaults;

        if (!nearly(s.carat.min, d.carat.min)) params.set('carat_min', formatNumber(s.carat.min, CARAT_STEP));
        if (!nearly(s.carat.max, d.carat.max)) params.set('carat_max', formatNumber(s.carat.max, CARAT_STEP));
        if (s.color.values.length) params.set('color', s.color.values.join(','));
        if (s.clarity.values.length) params.set('clarity', s.clarity.values.join(','));
        if (!nearly(s.price.min, d.price.min)) params.set('price_min', formatNumber(s.price.min, PRICE_STEP));
        if (!nearly(s.price.max, d.price.max)) params.set('price_max', formatNumber(s.price.max, PRICE_STEP));

        var query = params.toString();
        var current = String(window.location.search || '').replace(/^\?/, '');
        if (query === current) return;

        var url =
          window.location.pathname + (query ? '?' + query : '') + (window.location.hash || '');
        try {
          if (push && window.history && window.history.pushState) {
            window.history.pushState(null, '', url);
          } else if (window.history && window.history.replaceState) {
            window.history.replaceState(null, '', url);
          }
        } catch (error) {
          /* history can be unavailable in some embeds - filters still work */
        }
      }

      /* ------------------------------------------------------------------
         Filter controls
         ------------------------------------------------------------------ */

      setRange(group, side, value, fromControl) {
        var b = this.bounds[group];
        var state = this.filterState[group];
        var step = group === 'carat' ? CARAT_STEP : PRICE_STEP;

        if (isNaN(value)) return;
        value = clamp(roundTo(value, step), b.min, b.max);

        if (side === 'min') {
          state.min = Math.min(value, roundTo(state.max - step, step));
          if (state.min < b.min) state.min = b.min;
        } else {
          state.max = Math.max(value, roundTo(state.min + step, step));
          if (state.max > b.max) state.max = b.max;
        }

        if (state.min > state.max) {
          var swap = state.min;
          state.min = state.max;
          state.max = swap;
        }

        if (!fromControl) {
          this.syncSlider(group);
          this.syncInputs(group, null);
        }
        this.onFilterChanged(false);
      }

      syncSlider(group) {
        var slider = this.querySelector('[data-slider="' + group + '"]');
        if (!slider) return;
        var b = this.bounds[group];
        var state = this.filterState[group];
        var step = group === 'carat' ? CARAT_STEP : PRICE_STEP;
        var span = b.max - b.min || 1;
        var inputs = slider.querySelectorAll('[data-range]');

        for (var i = 0; i < inputs.length; i++) {
          var input = inputs[i];
          var side = input.dataset.range;
          var value = side === 'min' ? state.min : state.max;
          input.min = formatNumber(b.min, step);
          input.max = formatNumber(b.max, step);
          input.step = String(step);
          input.value = formatNumber(value, step);
          input.setAttribute('aria-valuemin', input.min);
          input.setAttribute('aria-valuemax', input.max);
          input.setAttribute('aria-valuenow', input.value);
          input.setAttribute(
            'aria-valuetext',
            (side === 'min' ? 'Minimum ' : 'Maximum ') + input.value
          );
        }

        var left = ((state.min - b.min) / span) * 100;
        var right = ((state.max - b.min) / span) * 100;
        var fill = slider.querySelector('[data-fill]');
        if (fill) {
          fill.style.left = clamp(left, 0, 100) + '%';
          fill.style.width = clamp(right - left, 0, 100) + '%';
        }
      }

      syncInputs(group, skipSide) {
        var state = this.filterState[group];
        var step = group === 'carat' ? CARAT_STEP : PRICE_STEP;
        var minEl = this.querySelector('[data-f="' + (group === 'carat' ? 'cmin' : 'pmin') + '"]');
        var maxEl = this.querySelector('[data-f="' + (group === 'carat' ? 'cmax' : 'pmax') + '"]');
        if (minEl && skipSide !== 'min') minEl.value = formatNumber(state.min, step);
        if (maxEl && skipSide !== 'max') maxEl.value = formatNumber(state.max, step);
      }

      buildValueButtons() {
        var self = this;
        ['color', 'clarity'].forEach(function (group) {
          var host = self.querySelector('[data-values="' + group + '"]');
          if (!host) return;

          var present = Object.create(null);
          self.rows.forEach(function (row) {
            var value = row[group];
            if (value != null && value !== '') present[String(value).toUpperCase()] = true;
          });

          var selected = self.filterState[group].values;
          var order = VALUE_ORDER[group].slice();

          host.innerHTML = order
            .map(function (value) {
              var on = selected.indexOf(value) > -1;
              var stocked = !!present[value];
              return (
                '<button type="button" class="elaris-diamond-filter__value' +
                (on ? ' is-active' : '') +
                '" data-group="' +
                group +
                '" data-value="' +
                esc(value) +
                '" aria-pressed="' +
                (on ? 'true' : 'false') +
                '"' +
                (stocked
                  ? ''
                  : ' disabled aria-disabled="true" title="No ' +
                    esc(value) +
                    ' diamonds available in ' +
                    esc(self.shapeLabel) +
                    '"') +
                '>' +
                esc(value) +
                '</button>'
              );
            })
            .join('');
        });
      }

      syncControls() {
        if (!this.boundsReady) return;
        this.syncSlider('carat');
        this.syncSlider('price');
        this.syncInputs('carat', null);
        this.syncInputs('price', null);
        this.setActiveFilter(this.filterState.activeFilter || 'carat');
        this.buildValueButtons();
      }

      onFilterChanged(push) {
        this.limit = this.pageSize;
        this.render();
        this.queueUrl(push);
      }

      resetFilters() {
        if (!this.defaults) return;
        var d = this.defaults;
        this.filterState.carat = { min: d.carat.min, max: d.carat.max };
        this.filterState.price = { min: d.price.min, max: d.price.max };
        this.filterState.color.values = [];
        this.filterState.clarity.values = [];
        this.sort = 'carat-asc';
        this.sortTouched = false;
        this.limit = this.pageSize;

        this.querySelector('[data-f="sort"]').value = 'carat-asc';
        this.syncControls();
        this.onFilterChanged(true);
      }

      /* ------------------------------------------------------------------
         Matching / sorting / rendering
         ------------------------------------------------------------------ */

      matches(row) {
        var s = this.filterState;

        if (s.carat.min != null && Number(row.carat) < s.carat.min - 1e-9) return false;
        if (s.carat.max != null && Number(row.carat) > s.carat.max + 1e-9) return false;

        if (s.price.min != null || s.price.max != null) {
          var price = Number(row.price_c) / 100;
          if (s.price.min != null && price < s.price.min - 1e-9) return false;
          if (s.price.max != null && price > s.price.max + 1e-9) return false;
        }

        if (s.color.values.length && s.color.values.indexOf(String(row.color).toUpperCase()) === -1) {
          return false;
        }
        if (
          s.clarity.values.length &&
          s.clarity.values.indexOf(String(row.clarity).toUpperCase()) === -1
        ) {
          return false;
        }
        return true;
      }

      sorted(rows) {
        var parts = this.sort.split('-');
        var key = parts[0];
        var direction = parts[1] === 'desc' ? -1 : 1;

        var compare;
        if (key === 'price') {
          compare = function (a, b) {
            return (a.price_c || 0) - (b.price_c || 0);
          };
        } else if (key === 'color') {
          compare = function (a, b) {
            return rank(COLOR_ORDER, a.color, b.color);
          };
        } else if (key === 'clarity') {
          compare = function (a, b) {
            return rank(CLARITY_ORDER, a.clarity, b.clarity);
          };
        } else if (key === 'match') {
          var reference = this.selected;
          compare = function (a, b) {
            if (reference) {
              var da = Math.abs((a.carat || 0) - (reference.carat || 0));
              var db = Math.abs((b.carat || 0) - (reference.carat || 0));
              if (Math.abs(da - db) > 1e-9) return da - db;
              var pa = Math.abs((a.price_c || 0) - (reference.price_c || 0));
              var pb = Math.abs((b.price_c || 0) - (reference.price_c || 0));
              if (pa !== pb) return pa - pb;
            }
            return (a.carat || 0) - (b.carat || 0);
          };
        } else {
          compare = function (a, b) {
            return (a.carat || 0) - (b.carat || 0);
          };
        }

        return rows
          .slice()
          .sort(function (a, b) {
            return compare(a, b) * direction;
          });
      }

      /* The row's own price until a stone is picked; after that every other row shows the
         difference against the selected stone, or "Same price" when there is none. */
      rowPrice(row, isPicked) {
        var reference = this.selected;
        if (isPicked || !reference) return { text: row.price, cls: '' };

        var diff = Number(row.price_c || 0) - Number(reference.price_c || 0);
        if (Math.abs(diff) < 1) return { text: 'Same price', cls: '' };

        var money = formatMoney(Math.abs(diff), this.dataset.moneyFormat, this.dataset.currency);
        return {
          text: (diff > 0 ? '+' : '-') + money,
          cls: diff > 0 ? ' is-up' : ' is-down'
        };
      }

      /* Direction only, and only for stones that sit close to the reference carat. */
      caratHint(row, isPicked) {
        var reference = this.selected;
        if (isPicked || !reference) return '';
        var delta = Number(row.carat || 0) - Number(reference.carat || 0);
        if (Math.abs(delta) < 0.005 || Math.abs(delta) > CARAT_HINT_MAX) return '';
        return delta > 0 ? '&uarr; Carat' : '&darr; Carat';
      }

      render() {
        var filtered = this.rows.filter(this.matches.bind(this));
        var ordered = this.sorted(filtered);
        var visible = ordered.slice(0, this.limit);
        var self = this;
        var reference = this.selected;

        this.rowsEl.innerHTML = visible
          .map(function (row) {
            var isPicked = !!(reference && String(reference.id) === String(row.id));
            var dims = dimensions(row);
            var hint = self.caratHint(row, isPicked);
            var price = self.rowPrice(row, isPicked);
            var badge = row.badge || (isPicked ? 'SELECTED' : '');
            var thumb = row.img
              ? '<img class="dbs__result-img" src="' +
                esc(row.img) +
                '" alt="" width="64" height="64" loading="lazy" decoding="async">'
              : '<span class="dbs__result-img" aria-hidden="true"></span>';
            var specs =
              Number(row.carat) +
              'ct - ' +
              row.color +
              ' - ' +
              row.clarity +
              (dims ? ' | ' + dims : '');
            var label =
              (isPicked ? 'Selected. ' : '') +
              'View ' +
              row.carat +
              ' carat ' +
              row.color +
              ' ' +
              row.clarity +
              ' ' +
              row.shape +
              ' lab-grown diamond, ' +
              row.price;

            return (
              '<div class="dbs__result' +
              (isPicked ? ' is-selected' : '') +
              '" role="button" tabindex="0" aria-pressed="' +
              (isPicked ? 'true' : 'false') +
              '" data-pick="' +
              esc(row.id) +
              '" aria-label="' +
              esc(label) +
              '">' +
              thumb +
              '<div class="dbs__result-body">' +
              (badge ? '<span class="dbs__badge">' + esc(badge) + '</span>' : '') +
              '<p class="dbs__specs">' +
              (hint ? '<span class="dbs__hint">' + hint + '</span>' : '') +
              esc(specs) +
              '</p>' +
              '<p class="dbs__result-price' +
              price.cls +
              '">' +
              esc(price.text) +
              '</p>' +
              '<span class="dbs__view">View details</span>' +
              '</div>' +
              (isPicked
                ? '<button type="button" class="dbs__clear dbs__result-action" data-clear ' +
                  'aria-label="Deselect this diamond">&times;</button>'
                : '<span class="dbs__result-action" aria-hidden="true">&rarr;</span>') +
              '</div>'
            );
          })
          .join('');

        var filteredCount = ordered.length;
        var total = this.rows.length;
        this.countEl.textContent = !total
          ? 'No diamonds available.'
          : filteredCount + ' diamonds available';

        this.emptyEl.hidden = filteredCount !== 0;
        this.moreEl.hidden = filteredCount <= this.limit;

        this.resetEls.forEach(function (button, index) {
          var insideEmptyState = index > 0;
          button.hidden = self.isDefault() && !(insideEmptyState && filteredCount === 0);
        });

        this.syncAddButton();
      }

      /* ------------------------------------------------------------------
         Diamond detail viewer
         ------------------------------------------------------------------ */

      /* `position: fixed` is resolved against the nearest ancestor that has a
         `transform`, `filter`, `perspective`, `contain` or `will-change`. The theme animates
         `.product__info-wrapper` (the block's parent) with `transform: translateY(2rem)`, so an
         overlay left inside the block is pinned to that column and never covers the page. The
         viewer is therefore re-parented to <body> inside a wrapper that keeps the block's scope
         class, so every `.dbs-<id> .dbs__…` rule in the stylesheet still matches. */
      relocateViewer() {
        var modal = this.mv('[data-modal]');
        this.modalEl = modal;
        if (!modal || !document.body) return;

        var scope = '';
        var classes = (this.getAttribute('class') || '').split(/\s+/);
        for (var i = 0; i < classes.length; i++) {
          if (classes[i] && classes[i].indexOf('dbs-') === 0) {
            scope = classes[i];
            break;
          }
        }
        if (!scope) return;

        var host = document.createElement('div');
        /* its own scope class, not the block's: `.dbs-<id>` carries the block's page padding,
           border-top and width, which would paint a stray bar into the body */
        host.className = 'dbs-viewer-' + scope.slice(4);
        host.setAttribute('data-dbs-viewer-root', '');
        document.body.appendChild(host);
        host.appendChild(modal);
        this.viewerRoot = host;
      }

      /* Query inside the viewer wherever it currently lives. */
      mv(selector) {
        return (this.viewerRoot || this).querySelector(selector);
      }

      mvs(selector) {
        return [].slice.call((this.viewerRoot || this).querySelectorAll(selector));
      }

      bindModalEvents() {
        var self = this;
        if (!this.modalEl) return;

        [].slice.call(this.modalEl.querySelectorAll('[data-modal-close]')).forEach(function (button) {
          button.addEventListener('click', function () {
            self.closeModal();
          });
        });

        /* Clicking outside closes. `mousedown` is tracked so that a text selection that starts
           inside the dialog and ends on the scrim does not throw the viewer away. */
        var self = this;
        this._downOutside = false;
        var markDown = function (event) {
          self._downOutside = !self.modalDialogEl || !self.modalDialogEl.contains(event.target);
        };
        [this.modalEl, this.viewerRoot].forEach(function (node) {
          if (!node) return;
          node.addEventListener('mousedown', markDown);
          node.addEventListener('touchstart', markDown, { passive: true });
        });
        var onOutside = function (event) {
          if (event.target.closest && event.target.closest('.dbs__modal-dialog')) return;
          if (!self._downOutside && event.type === 'click') return;
          self.closeModal();
        };
        if (this.modalEl) this.modalEl.addEventListener('click', onOutside);
        if (this.viewerRoot) this.viewerRoot.addEventListener('click', onOutside);

        if (this.modalZoomEl) {
          this.modalZoomEl.addEventListener('click', function (event) {
            self.toggleZoom(event);
          });
          this.modalZoomEl.addEventListener('mousemove', function (event) {
            self.moveZoom(event);
          }, { passive: true });

          // The cached box goes stale when the viewport or scroll position
          // changes while the modal is open.
          var remeasure = function () {
            self.measureZoomBox();
          };
          window.addEventListener('resize', remeasure);
          window.addEventListener('scroll', remeasure, { passive: true });
        }

        if (this.modalImgEl) {
          this.modalImgEl.addEventListener('error', function () {
            self.showModalImage(false);
          });
          this.modalImgEl.addEventListener('load', function () {
            self.showModalImage(true);
          });
        }

        if (this.modalCtaEl) {
          this.modalCtaEl.addEventListener('click', function () {
            self.chooseActiveDiamond();
          });
        }

        if (this.modalWhyEl) {
          this.modalWhyEl.addEventListener('click', function () {
            self.toggleTip(self.modalTipWhyEl);
          });
        }

        if (this.modalPropEl) {
          this.modalPropEl.addEventListener('click', function () {
            self.toggleTip(self.modalTipPropEl);
          });
        }

        /* the education strip is open on desktop and collapsed behind a tap on phones */
        var eduToggle = this.mv('[data-edu-toggle]');
        var eduWrap = this.mv('[data-edu-wrap]');
        if (eduToggle && eduWrap) {
          eduToggle.addEventListener('click', function () {
            var open = eduWrap.classList.toggle('is-open');
            eduToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          });
        }

        /* media tabs - only rendered when the feed supplies a video or a 360 viewer */
        [].slice.call(this.mvs('[data-modal-tab]')).forEach(function (tab) {
          tab.addEventListener('click', function () {
            self.setMediaTab(tab.dataset.modalTab);
          });
        });
      }

      isPicked(row) {
        return !!(this.selected && String(this.selected.id) === String(row.id));
      }

      openModal(row, opener) {
        if (!row || !this.modalEl) return;
        this.activeDiamond = row;
        this.modalOpener = opener || (document.activeElement !== this ? document.activeElement : null);
        clearTimeout(this._closeTimer);
        this.modalEl.classList.remove('is-closing');
        this.renderModal();
        this.modalEl.hidden = false;
        this.modalOpen = true;
        this.measureZoomBox();
        this._prevOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        if (document.addEventListener) document.addEventListener('keydown', this._onDocKey);
        if (this.modalScrollEl) this.modalScrollEl.scrollTop = 0;
        if (this.modalDialogEl) this.modalDialogEl.focus();
      }

      closeModal() {
        if (!this.modalEl || !this.modalOpen) return;

        var finish = function () {
          self.modalEl.classList.remove('is-closing');
          self.modalEl.hidden = true;
          if (self.modalScrollEl) self.modalScrollEl.scrollTop = 0;
        };
        var self = this;

        this.modalOpen = false;
        this.activeDiamond = null;
        this.setZoom(false);
        if (this.modalVideoEl && this.modalVideoEl.pause) this.modalVideoEl.pause();
        document.documentElement.style.overflow = this._prevOverflow || '';
        if (document.removeEventListener) document.removeEventListener('keydown', this._onDocKey);

        var opener = this.modalOpener;
        this.modalOpener = null;
        if (opener && document.contains && document.contains(opener) && typeof opener.focus === 'function') {
          opener.focus();
        }

        /* fade + slight scale out, then hide (240ms, matching the CSS) */
        var reduced = false;
        if (window.matchMedia) {
          try {
            reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          } catch (e) {
            reduced = false;
          }
        }
        if (reduced) {
          finish();
          return;
        }
        this.modalEl.classList.add('is-closing');
        clearTimeout(this._closeTimer);
        this._closeTimer = setTimeout(finish, 240);
      }

      _onDocKey(event) {
        if (!this.modalOpen) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          if (this.zoomOn) {
            this.setZoom(false);
            return;
          }
          this.closeModal();
          return;
        }
        if (event.key !== 'Tab') return;
        var focusables = this.modalEl.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex="0"], input, select, textarea'
        );
        if (!focusables.length) return;
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        var inside = this.modalEl.contains(document.activeElement);
        if (!inside) {
          event.preventDefault();
          first.focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      /* ------------------------------------------------------------------
         Data model

         Everything the viewer shows comes from this one normalised object.
         Nothing about an individual diamond is written into the markup.
         ------------------------------------------------------------------ */

      normalizeDiamond(row) {
        var clean = function (value) {
          if (value === null || value === undefined) return '';
          var text = String(value).trim();
          if (!text) return '';
          if (text === '-' || text.toUpperCase() === 'N/A') return '';
          if (text.toLowerCase() === 'null' || text.toLowerCase() === 'undefined') return '';
          return text;
        };

        var dims = this.dimensionParts(row);
        var dimensions = dims ? dims[0] + ' \u00d7 ' + dims[1] + ' \u00d7 ' + dims[2] + ' mm' : '';
        var ratio = Number(row.ratio);
        if (!ratio && dims && Number(dims[1]) > 0) {
          ratio = Number(dims[0]) / Number(dims[1]);
        }
        if (!isFinite(ratio) || ratio <= 0) ratio = 0;

        return {
          id: row.id,
          sku: clean(row.sku),
          type: clean(row.type),
          shape: clean(row.shape),
          carat: clean(row.carat),
          certificate: clean(row.report),
          color: clean(row.color).toUpperCase(),
          clarity: clean(row.clarity).toUpperCase(),
          cut: clean(row.cut),
          polish: clean(row.polish),
          symmetry: clean(row.symmetry),
          fluorescence: clean(row.fluo),
          dimensions: dimensions,
          ratio: ratio ? ratio.toFixed(2) : '',
          lab: clean(row.lab),
          image: clean(row.img_lg) || clean(row.img),
          thumbnail: clean(row.img),
          video: clean(row.video),
          viewer360: clean(row.viewer360),
          certificate_url: clean(row.certificateUrl),
          price_c: Number(row.price_c) || 0,
          price: clean(row.price),
          badge: clean(row.badge)
        };
      }

      /* --- modal rendering --- */

      renderModal() {
        var row = this.activeDiamond;
        if (!row) return;
        var self = this;
        var d = this.normalizeDiamond(row);
        var isPicked = this.isPicked(row);

        /* header */
        this.modalTitleEl.textContent = d.type || 'Lab Grown Diamond';
        if (this.modalSubtitleEl) {
          this.modalSubtitleEl.textContent = [d.carat ? d.carat + ' ct' : '', d.color, d.clarity, d.shape]
            .filter(Boolean)
            .join(' \u00b7 ');
        }

        var badge = d.badge || (isPicked ? 'Selected' : '');
        if (badge) {
          this.modalBadgeEl.textContent = badge;
          this.modalBadgeEl.hidden = false;
        } else {
          this.modalBadgeEl.hidden = true;
        }

        var price = this.rowPrice(row, isPicked);
        this.modalPriceEl.textContent = price.text;
        this.modalPriceEl.className = 'dbs__modal-price' + price.cls;

        /* certificate badge - only ever claims what the feed actually supplies */
        this.renderCertBadge(d);

        /* media */
        this.renderMedia(d);

        /* specification table */
        this.modalSpecsEl.innerHTML = this.specRows(d)
          .map(function (pair) {
            return (
              '<div class="dbs__spec-row"><dt>' +
              esc(pair[0]) +
              '</dt><dd>' +
              (pair[2] || esc(pair[1])) +
              '</dd></div>'
            );
          })
          .join('');

        /* quick info - only when it can be answered from real data */
        var showProportions = !!d.dimensions;
        var showWhy = !isPicked && !!this.selected;
        this.modalPropEl.hidden = !showProportions;
        this.modalWhyEl.hidden = !showWhy;
        this.modalQuickEl.hidden = !(showProportions || showWhy);
        this.modalTipPropEl.hidden = true;
        this.modalTipWhyEl.hidden = true;
        if (showProportions) {
          this.modalTipPropEl.textContent =
            'Length ' +
            this.dimensionParts(row)[0] +
            'mm, width ' +
            this.dimensionParts(row)[1] +
            'mm, depth ' +
            this.dimensionParts(row)[2] +
            'mm' +
            (d.ratio ? '. Length to width ratio ' + d.ratio + '.' : '.');
        }
        if (showWhy) this.modalTipWhyEl.textContent = this.whyThisDiamond(row);

        /* education cards */
        this.renderEducation(d);

        /* price breakdown */
        this.renderPriceBox(d);

        this.setModalNotice('', false);
        this.syncModalCta();
      }

      renderCertBadge(d) {
        var badge = this.modalCertBadgeEl;
        var button = this.modalCertBtnEl;
        var linked = !!d.certificate_url;
        var name = this.mv('[data-modal-cert-badge-name]');
        var no = this.mv('[data-modal-cert-badge-no]');

        if (badge) {
          /* Both the grading lab and the report number are supplier data and are present on every
             stone in the catalog, so the badge can always state the real lab. It must NOT hardcode
             IGI: 46 of the 3,130 stones are graded by GIA. */
          if (!d.lab) {
            badge.hidden = true;
            badge.removeAttribute('href');
          } else {
            badge.hidden = false;
            if (name) name.textContent = d.lab + ' certified';
            if (no) no.textContent = d.certificate || '';

            if (linked && d.certificate) {
              badge.href = d.certificate_url;
              badge.classList.remove('dbs__cert-badge--static');
            } else {
              /* No stored certificate document, so the badge reports the lab without pretending to
                 be a link. A gold pill that does nothing when tapped is worse than no pill. */
              badge.removeAttribute('href');
              badge.classList.add('dbs__cert-badge--static');
            }
          }
        }

        if (button) {
          if (linked) {
            button.hidden = false;
            button.href = d.certificate_url;
            button.textContent = 'View certificate';
          } else {
            button.hidden = true;
            button.removeAttribute('href');
          }
        }
      }

      renderMedia(d) {
        var stage = this.modalZoomEl;
        var tabsEl = this.mv('[data-modal-tabs]');
        var video = this.mv('[data-modal-video]');
        var img = this.modalImgEl;
        var hasVideo = !!d.video;
        var has360 = !!d.viewer360;

        if (video) {
          if (hasVideo && video.getAttribute('src') !== d.video) {
            video.setAttribute('src', d.video);
          }
          video.hidden = true;
        }

        if (d.image) {
          img.alt =
            d.carat + ' carat ' + d.color + ' ' + d.clarity + ' ' + d.shape + ' lab grown diamond';
          if (img.getAttribute('src') !== d.image) img.setAttribute('src', d.image);
          img.hidden = false;
          this.showModalImage(true);
        } else {
          img.removeAttribute('src');
          this.showModalImage(false);
          this.modalFallbackEl.textContent = [d.carat ? d.carat + ' carat' : '', d.shape]
            .filter(Boolean)
            .join(' ') + ' lab grown diamond';
          this.modalFallbackEl.hidden = false;
        }
        this.setZoom(false);
        this.mediaTab = 'image';

        var tabVideo = this.mv('[data-modal-tab="video"]');
        var tabRotate = this.mv('[data-modal-tab="rotate"]');
        if (tabVideo) tabVideo.hidden = !hasVideo;
        if (tabRotate) tabRotate.hidden = !has360;
        if (tabsEl) tabsEl.hidden = !(hasVideo || has360);
        if (stage) stage.setAttribute('aria-label', hasVideo ? 'Diamond media' : 'Zoom diamond image');
      }

      setMediaTab(name) {
        var img = this.modalImgEl;
        var video = this.mv('[data-modal-video]');
        var hasVideo = !!(this.activeDiamond && this.activeDiamond.video);
        var has360 = !!(this.activeDiamond && this.activeDiamond.viewer360);
        if (name === 'video' && hasVideo) {
          if (video) video.hidden = false;
          img.hidden = true;
          this.mediaTab = 'video';
        } else if (name === 'rotate' && has360) {
          if (video) {
            video.hidden = true;
            if (video.pause) video.pause();
          }
          img.hidden = false;
          this.mediaTab = 'rotate';
        } else {
          if (video) {
            video.hidden = true;
            if (video.pause) video.pause();
          }
          img.hidden = false;
          this.mediaTab = 'image';
        }
        [].slice.call(this.mvs('[data-modal-tab]')).forEach(function (tab) {
          tab.classList.toggle('is-active', tab.dataset.modalTab === this.mediaTab);
        }, this);
      }

      /* --- education cards --- */

      renderEducation(d) {
        var set = function (key, text) {
          var el = this.mv('[data-edu-title="' + key + '"]');
          if (el) el.textContent = text;
        }.bind(this);

        if (d.carat) set('carat', 'Carat: ' + d.carat);
        if (d.color) set('color', 'Color: ' + d.color);
        if (d.clarity) set('clarity', 'Clarity: ' + d.clarity);
        if (d.dimensions) set('dimensions', 'Dimensions: ' + d.dimensions);

        var card = function (key) {
          return this.mv('[data-edu="' + key + '"]');
        }.bind(this);

        var caratCard = card('carat');
        if (caratCard) caratCard.hidden = !d.carat;
        var colorCard = card('color');
        if (colorCard) colorCard.hidden = !d.color;
        var clarityCard = card('clarity');
        if (clarityCard) clarityCard.hidden = !d.clarity;
        var dimCard = card('dimensions');
        if (dimCard) dimCard.hidden = !d.dimensions;

        this.renderScale('color', COLOR_SCALE, d.color);
        this.renderScale('clarity', CLARITY_SCALE, d.clarity);

        var ratioEl = this.mv('[data-edu-ratio]');
        if (ratioEl) {
          if (d.ratio) {
            ratioEl.textContent = 'L/W ratio: ' + d.ratio + '. For a ' + (d.shape || 'round') +
              ' diamond the length to width ratio is the length divided by the width.';
          } else {
            ratioEl.textContent =
              'Measurements for this stone are not available yet, so no ratio is calculated.';
          }
        }
      }

      renderScale(kind, scale, current) {
        var el = this.mv('[data-edu-scale="' + kind + '"]');
        if (!el) return;
        var currentGrade = String(current || '').toUpperCase();
        var available = this.availableGrades(kind);
        el.innerHTML = scale
          .map(function (grade) {
            var cls = 'dbs__scale-btn';
            if (grade === currentGrade) cls += ' is-current';
            else if (available && available.indexOf(grade) === -1) cls += ' is-unavailable';
            var note =
              grade === currentGrade
                ? ' aria-current="true"'
                : '';
            return '<span class="' + cls + '"' + note + '>' + esc(grade) + '</span>';
          })
          .join('');
      }

      /* Which grades this shape actually has in stock, so the scales never imply
         that an unavailable grade is being offered. */
      availableGrades(kind) {
        var field = kind === 'color' ? 'color' : 'clarity';
        var seen = {};
        var rows = this.rows || [];
        for (var i = 0; i < rows.length; i++) {
          var value = String(rows[i][field] || '').trim().toUpperCase();
          if (value) seen[value] = true;
        }
        var keys = Object.keys(seen);
        return keys.length ? keys : null;
      }

      /* --- price breakdown --- */

      renderPriceBox(d) {
        var box = this.mv('[data-modal-price-box]');
        if (!box) return;

        var settingC = parseInt(this.dataset.settingPriceC, 10) || 0;
        var settingCompareC = parseInt(this.dataset.settingCompareC, 10) || 0;
        var diamondC = d.price_c;

        var showDiamond = diamondC > 0;
        var showSetting = settingC > 0;
        var discount = settingCompareC > settingC ? settingCompareC - settingC : 0;
        var showSave = discount > 0;
        var showTotal = showDiamond || showSetting;

        var rowDiamond = this.mv('[data-modal-row-diamond]');
        var rowSetting = this.mv('[data-modal-row-setting]');
        var rowSave = this.mv('[data-modal-row-save]');
        var rowTotal = this.mv('[data-modal-row-total]');

        if (rowDiamond) rowDiamond.hidden = !showDiamond;
        if (rowSetting) rowSetting.hidden = !showSetting;
        if (rowSave) rowSave.hidden = !showSave;
        if (rowTotal) rowTotal.hidden = !showTotal;
        box.hidden = !showTotal;
        if (!showTotal) return;

        var money = function (cents) {
          return formatMoney(Math.abs(cents), this.dataset.moneyFormat, this.dataset.currency);
        }.bind(this);

        var outDiamond = this.mv('[data-modal-price-diamond]');
        if (outDiamond) outDiamond.textContent = money(diamondC);
        var outSetting = this.mv('[data-modal-price-setting]');
        if (outSetting) outSetting.textContent = money(settingC);
        var outSave = this.mv('[data-modal-price-save]');
        if (outSave) outSave.textContent = '-' + money(discount);
        var outTotal = this.mv('[data-modal-price-total]');
        if (outTotal) {
          outTotal.textContent = money(diamondC + settingC - discount);
        }
      }

      specRows(d) {
        var pairs = [];
        var push = function (label, value, html) {
          if (value === null || value === undefined) return;
          var text = String(value).trim();
          if (!text) return;
          if (text === '-' || text === 'N/A' || text === 'null' || text === 'undefined') return;
          pairs.push([label, text, html]);
        };

        push('Type', d.type);
        push('Shape', d.shape);
        push('Carat', d.carat ? d.carat + ' ct' : '');
        push('Certificate', d.certificate);
        push('Color', d.color);
        push('Clarity', d.clarity);
        push('Cut', d.cut);
        push('Polish', d.polish);
        push('Symmetry', d.symmetry);
        push('Fluorescence', d.fluorescence);
        push('Dimensions', d.dimensions);
        push('L/W ratio', d.ratio);
        push('SKU', d.sku);
        push('Lab', d.lab);

        return pairs;
      }

      dimensionParts(row) {
        var numbers = String(row.measure || '').match(/[0-9]+(?:\.[0-9]+)?/g);
        if (!numbers || numbers.length < 3) return null;
        return [numbers[0], numbers[1], numbers[2]];
      }

      whyThisDiamond(row) {
        var reference = this.selected;
        if (!reference) return '';
        var lines =
          'Your chosen stone is ' +
          reference.carat +
          'ct ' +
          reference.color +
          ' ' +
          reference.clarity +
          ' at ' +
          reference.price +
          '.';
        var caratDiff = Number(row.carat) - Number(reference.carat);
        if (caratDiff) {
          lines +=
            ' This one is ' +
            Math.abs(caratDiff).toFixed(2) +
            'ct ' +
            (caratDiff > 0 ? 'heavier' : 'lighter') +
            '.';
        } else {
          lines += ' This one has the same carat weight.';
        }
        var priceDiff = Number(row.price_c) - Number(reference.price_c);
        if (priceDiff) {
          lines +=
            ' It is ' +
            formatMoney(Math.abs(priceDiff), this.dataset.moneyFormat, this.dataset.currency) +
            (priceDiff > 0 ? ' more.' : ' less.');
        } else {
          lines += ' It is the same price.';
        }
        return lines;
      }

      syncModalCta() {
        if (!this.modalCtaEl || !this.activeDiamond) return;
        var isPicked = this.isPicked(this.activeDiamond);
        var price = this.rowPrice(this.activeDiamond, isPicked);
        var label = isPicked ? 'Selected' : 'Select this diamond';
        if (price.cls) label += ' | ' + price.text;
        this.modalCtaEl.textContent = label;
      }

      setModalNotice(message, isError) {
        if (!this.modalNoticeEl) return;
        if (!message) {
          this.modalNoticeEl.hidden = true;
          this.modalNoticeEl.textContent = '';
          this.modalNoticeEl.classList.remove('is-error');
          return;
        }
        this.modalNoticeEl.hidden = false;
        this.modalNoticeEl.textContent = message;
        this.modalNoticeEl.classList.toggle('is-error', !!isError);
      }

      showModalImage(ok) {
        if (!this.modalImgEl || !this.modalFallbackEl) return;
        this.modalImgEl.hidden = !ok;
        this.modalFallbackEl.hidden = !!ok;
        if (this.modalHintEl) this.modalHintEl.hidden = !ok;
      }

      /* --- zoom --- */

      setZoom(on, event) {
        if (!this.modalImgEl) return;
        this.zoomOn = !!on;
        this.modalImgEl.classList.toggle('is-zoomed', this.zoomOn);
        this.modalZoomEl.classList.toggle('is-zoomed', this.zoomOn);
        if (!this.zoomOn) {
          this.modalImgEl.style.transformOrigin = 'center center';
          this.modalImgEl.style.transform = '';
        } else if (event) {
          this.setZoomOrigin(event);
        }
      }

      toggleZoom(event) {
        if (this.mediaTab === 'video') return;
        // Re-measure before the first zoom write so a cached box from a previous
        // open (different scroll position, resized viewport) is never reused.
        this.measureZoomBox();
        this.setZoom(!this.zoomOn, event);
      }

      setZoomOrigin(event) {
        if (!this.modalImgEl || !event) return;
        var box = this.zoomBox;
        if (!box || !box.width || !box.height) return;
        var x = ((event.clientX - box.left) / box.width) * 100;
        var y = ((event.clientY - box.top) / box.height) * 100;
        this.modalImgEl.style.transformOrigin =
          Math.max(0, Math.min(100, x)).toFixed(2) + '% ' + Math.max(0, Math.min(100, y)).toFixed(2) + '%';
        this.modalImgEl.style.transform = 'scale(2)';
      }

      /* The zoom image's box only changes when the modal opens, the viewport
         changes, or the page scrolls -- never because the pointer moved. So it
         is measured once and reused, instead of calling getBoundingClientRect()
         on every mousemove (a forced synchronous layout per mouse event). */
      measureZoomBox() {
        this.zoomBox = this.modalImgEl ? this.modalImgEl.getBoundingClientRect() : null;
      }

      moveZoom(event) {
        if (!this.zoomOn) return;
        if (!this.zoomBox) return;

        // Coalesce to one write per frame; mousemove can fire far faster.
        var self = this;
        this.zoomPoint = { x: event.clientX, y: event.clientY };
        if (this.zoomFrame) return;
        this.zoomFrame = requestAnimationFrame(function () {
          self.zoomFrame = null;
          if (!self.zoomPoint) return;
          self.setZoomOrigin(self.zoomPoint);
        });
      }

      toggleTip(tip) {
        if (!tip) return;
        tip.hidden = !tip.hidden;
      }

      /* --- availability + choose --- */

      /* The feed is the source of truth, so confirm the stone is still on sale (and still
         costs the same) before it is attached to the ring. */
      validateDiamond(row) {
        if (!row) return Promise.resolve({ ok: false, reason: 'gone' });
        if (row.available === false) return Promise.resolve({ ok: false, reason: 'unavailable' });
        if (row.page === undefined || row.page === null) return Promise.resolve({ ok: true, row: row });

        var self = this;
        return this.fetchPage(this.shape, Number(row.page)).then(
          function (chunk) {
            for (var i = 0; i < chunk.length; i++) {
              if (String(chunk[i].id) === String(row.id)) {
                var fresh = chunk[i];
                fresh.page = row.page;
                if (fresh.available === false) return { ok: false, reason: 'unavailable' };
                if (Number(fresh.price_c) !== Number(row.price_c)) {
                  return { ok: false, reason: 'price', fresh: fresh };
                }
                return { ok: true, row: row };
              }
            }
            return { ok: false, reason: 'gone' };
          },
          function () {
            /* we cannot verify, so we must not sell it */
            return { ok: false, reason: 'error' };
          }
        );
      }

      chooseActiveDiamond() {
        var row = this.activeDiamond;
        if (!row || this.validating) return;
        var self = this;
        this.validating = true;
        this.setModalNotice('', false);
        this.modalCtaEl.disabled = true;
        this.modalCtaEl.textContent = 'Checking availability...';

        this.validateDiamond(row).then(function (result) {
          self.validating = false;
          self.modalCtaEl.disabled = false;
          self.syncModalCta();

          if (result.ok) {
            var id = String(result.row.id);
            self.select(result.row);
            self.closeModal();
            var again = self.querySelector('[data-pick="' + id + '"]');
            if (again && typeof again.focus === 'function') again.focus();
            return;
          }

          if (result.reason === 'price' && result.fresh) {
            /* keep the shopper in the viewer: the new price has to be confirmed on screen */
            for (var key in result.fresh) {
              if (Object.prototype.hasOwnProperty.call(result.fresh, key)) {
                self.activeDiamond[key] = result.fresh[key];
              }
            }
            self.renderModal();
            self.setModalNotice('The price for this diamond has changed. Please confirm to continue.', true);
            return;
          }
          if (result.reason === 'unavailable' || result.reason === 'gone') {
            self.setModalNotice('This diamond is no longer available. Please choose another diamond.', true);
            return;
          }
          self.setModalNotice('We could not confirm this diamond is still available. Please try again.', true);
        });
      }

      restoreSelection(rows) {
        if (!this.selected) return;
        var match = null;
        for (var i = 0; i < rows.length; i++) {
          if (String(rows[i].id) === String(this.selected.id)) {
            match = rows[i];
            break;
          }
        }
        if (match) this.selected = match;
        this.syncSelectedPanel();
      }

      /* ------------------------------------------------------------------
         Selection / cart (unchanged)
         ------------------------------------------------------------------ */

      select(row) {
        this.selected = row;
        selection[this.productId] = {
          row: row,
          shape: this.shape,
          shapeLabel: this.shapeLabel
        };

        /* Default order becomes "closest to the selected stone" as soon as there is one,
           unless the shopper already picked an order themselves. */
        if (!this.sortTouched && this.sort === 'carat-asc') {
          this.sort = 'match';
          var sortInput = this.querySelector('[data-f="sort"]');
          if (sortInput) sortInput.value = 'match';
        }

        this.syncSelectedPanel();
        this.syncOpenButton();
        this.render();
        this.setStatus('');
      }

      clearSelection() {
        this.selected = null;
        delete selection[this.productId];
        this.syncSelectedPanel();
        this.syncOpenButton();
        this.render();
      }

      syncSelectedPanel() {
        var row = this.selected;
        if (!row) {
          this.selectedEl.hidden = true;
          this.syncAddButton();
          return;
        }

        var image = this.querySelector('[data-sel-img]');
        if (row.img) {
          image.src = row.img;
          image.hidden = false;
        } else {
          image.removeAttribute('src');
          image.hidden = true;
        }

        this.querySelector('[data-sel-name]').textContent =
          row.carat + ' ct ' + row.color + ' ' + row.clarity + ' ' + row.shape;
        this.querySelector('[data-sel-meta]').textContent =
          [row.cut, row.lab, row.report].filter(Boolean).join(' | ');
        this.querySelector('[data-sel-price]').textContent = row.price;
        this.selectedEl.hidden = false;
        this.syncAddButton();
      }

      syncAddButton() {
        var enabled = !!this.selected && !this.busy;
        if (this.addEl) {
          this.addEl.disabled = !enabled;
          this.addEl.classList.toggle('is-loading', this.busy);
          this.addEl.textContent = this.busy ? 'Adding to cart...' : 'Add ring and diamond to cart';
        }
        this.syncRingButton();
      }

      /* There is only ONE add-to-cart button on the page: the ring's own. When a diamond is
         chosen it is relabelled so the customer knows the cart will hold both.

         The theme's Shop Pay / accelerated-payment button stays ON the page, because it is a real
         payment option the store offers. It cannot be used while a diamond is selected though: it
         submits a single variant, so it would take payment for the setting and silently drop the
         diamond. Rather than hide the option, it is shown but made non-clickable with the reason
         spelled out, and re-enabled the moment the diamond is removed. */
      syncRingButton() {
        if (!this.submitLabel) return;
        var has = !!this.selected;
        var wanted = has ? 'Add ring + diamond to cart' : this.originalSubmitLabel || 'Add to cart';
        if (this.submitLabel.textContent.trim() !== wanted) this.submitLabel.textContent = wanted;
        this.syncPaymentButton(has);
      }

      syncPaymentButton(blocked) {
        var pay = document.querySelector('.shopify-payment-button');
        if (!pay) return;
        pay.classList.toggle('dbs-pay-blocked', !!blocked);
        if (blocked) pay.setAttribute('aria-disabled', 'true');
        else pay.removeAttribute('aria-disabled');
        var note = this.ensurePaymentNote(pay);
        note.hidden = !blocked;
      }

      ensurePaymentNote(pay) {
        var note = document.querySelector('[data-dbs-pay-note]');
        if (!note) {
          note = document.createElement('p');
          note.setAttribute('data-dbs-pay-note', '');
          note.className = 'dbs-pay-note';
          note.setAttribute('role', 'status');
          note.textContent =
            'Shop Pay is unavailable while a diamond is selected, because it can only add the ring on its own. ' +
            'Use Add to cart to add the ring and your diamond together.';
          if (pay.parentNode) pay.parentNode.insertBefore(note, pay.nextSibling);
        }
        return note;
      }

      /* The theme's product-form.js handles the ring's submit itself. We sit in front of it at the
         capture phase on `document`, so when a diamond is selected the submit is taken over: the
         diamond is added first, then the form is re-submitted and the theme adds the ring and
         renders the cart drawer exactly as it always has. product-form.js is not modified. */
      bindFormInterception() {
        var self = this;
        var form = document.getElementById(this.dataset.productForm);
        if (!form) return;
        this.ringForm = form;

        var submit = form.querySelector('[type="submit"]');
        this.submitLabel = submit ? submit.querySelector('span') : null;
        this.originalSubmitLabel = this.submitLabel ? this.submitLabel.textContent.trim() : '';

        document.addEventListener('submit', function (event) {
          if (!self.selected || self._diamondInFlight || self.busy) return;
          if (event.target !== form) return;
          event.preventDefault();
          event.stopPropagation();
          self._diamondInFlight = true;
          self.addBoth();
        }, true);

        this.syncRingButton();
      }

      setBusy(value) {
        this.busy = value;
        this.syncAddButton();
      }

      setStatus(message) {
        this.statusEl.textContent = message || '';
      }

      addBoth() {
        var self = this;
        var row = this.selected;
        if (!row || this.busy) return;

        var form = document.getElementById(this.dataset.productForm);
        if (!form) {
          this.setStatus('We could not find this ring\u2019s form. Please refresh the page and try again.');
          return;
        }

        var idInput = form.querySelector('[name="id"]');
        if (!idInput || !idInput.value || idInput.disabled) {
          this.setStatus('Choose your ring size and metal first.');
          return;
        }

        this.setStatus('');
        this.setBusy(true);

        var routes = window.routes || {};
        var addUrl = (routes.cart_add_url || '/cart/add') + '.js';

        fetch(addUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [
              {
                id: row.id,
                quantity: 1,
                properties: { 'Goes with': self.dataset.productTitle }
              }
            ]
          })
        })
          .then(function (response) {
            if (!response.ok) {
              return response
                .json()
                .catch(function () {
                  return {};
                })
                .then(function (payload) {
                  throw new Error(payload.description || payload.message || 'The diamond could not be added to your cart.');
                });
            }
            self.setStatus('');
            /* release the guard so the re-submitted event reaches the theme's own handler */
            self._diamondInFlight = false;
            self.submitRing(form);
          })
          .catch(function (error) {
            self._diamondInFlight = false;
            self.setBusy(false);
            self.setStatus(error.message || 'Something went wrong. Please try again.');
          });
      }

      submitRing(form) {
        try {
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
            return;
          }
        } catch (error) {
          /* older engines without a working requestSubmit fall through */
        }
        var button = form.querySelector('[type="submit"]');
        if (button) {
          button.click();
          return;
        }
        form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    }
  );
})();
